import { isAxiosError } from "axios";
// `retryAfterSeconds` vive in `error.ts` perché la dashboard freelance legge lo stesso
// header con le stesse tre forme (secondi, stringa numerica, data HTTP): due copie della
// stessa aritmetica divergerebbero al primo bordo.
import { getApiResponseBodyMessage, retryAfterSeconds } from "./error";

export type SemanticSearchErrorKind =
  | { kind: "invalid_query" }
  | { kind: "invalid_offer" }
  | { kind: "rate_limited"; retryAfterSeconds?: number }
  | { kind: "semantic_unavailable" }
  | { kind: "timeout" }
  | { kind: "generic"; message: string };

const epochResetSeconds = (value: unknown): number | undefined => {
  const epochSeconds =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : Number.NaN;
  if (!Number.isFinite(epochSeconds)) return undefined;
  return Math.max(0, Math.ceil(epochSeconds - Date.now() / 1000));
};

export const mapSemanticSearchError = (
  error: unknown,
): SemanticSearchErrorKind => {
  // Timeout client axios (nessuna risposta dal server).
  if (isAxiosError(error) && error.code === "ECONNABORTED") {
    return { kind: "timeout" };
  }

  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (
      error as {
        response?: {
          status?: number;
          headers?: Record<string, unknown>;
        };
      }
    ).response;

    if (response?.status === 400) return { kind: "invalid_query" };
    if (response?.status === 404) return { kind: "invalid_offer" };
    if (response?.status === 429) {
      return {
        kind: "rate_limited",
        retryAfterSeconds:
          retryAfterSeconds(
            response.headers?.["retry-after"] ??
              response.headers?.["Retry-After"],
          ) ??
          retryAfterSeconds(
            response.headers?.["ratelimit-reset"] ??
              response.headers?.["RateLimit-Reset"],
          ) ??
          epochResetSeconds(
            response.headers?.["x-ratelimit-reset"] ??
              response.headers?.["X-RateLimit-Reset"],
          ),
      };
    }
    if (response?.status === 502) return { kind: "semantic_unavailable" };
  }

  // Solo il messaggio dal body BE: mai il testo tecnico di `error.message`
  // (i consumer cadono sulla chiave localizzata quando la stringa è vuota).
  return {
    kind: "generic",
    message: getApiResponseBodyMessage(error),
  };
};
